import jsonAutocomplete from 'json-autocomplete';
import { getWritingPlan } from 'src/configuration/writing-plans';
import {
  Assignment,
  BlockType,
  BlockTypesEnum,
  WritingPlan,
} from 'src/types/models';
import API from './config';
import { DOCUMENT_STARTERS } from 'src/configuration/starters';

// All OpenAI access is now server-side.

function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(';').shift();
  return undefined;
}

// Helper function to resolve language codes, detecting browser language when needed
function resolveLanguageCode(languageCode: string): string {
  if (languageCode === 'browser-language') {
    const browserLang =
      navigator.language || navigator.languages?.[0] || 'en-US';
    return browserLang;
  }
  return languageCode;
}

async function textFromTopic(topic: string): Promise<string> {
  const API_KEY = '134cb3dac9xa6b386d7bx4d8a6daa34a49897x3e90e'.replace(
    /x/g,
    '',
  );

  let text: string;
  try {
    // Check whether topic is a URL. This will raise an exception if
    // it is not.
    const url = new URL(topic.trim());
    const response = await fetch(
      `https://extractorapi.com/api/v1/extractor/?apikey=${API_KEY}&url=${url}`
    );
    const data = await response.json();
    text = `# ${data.title}\n\n` + `[Source](${data.url})\n\n${data.text}`;
  } catch (_) {
    text = topic;
  }

  // Return the first 30,000 characters of the text.
  return text.substring(0, 30000);
}
export async function suggestImportantWordsForPrompt(
  text: string,
  topic: string,
  writingPlan: string,
  gradeLevel = '4th'
) {
  return API.post<{
    data: {
      words: string[];
    }[];
  }>('/ai/important-words', {
    text,
    topic,
    grade_level: gradeLevel,
    writing_plan: writingPlan,
  });
}

export async function* generateBaseDocument(
  topic: string,
  templates: typeof DOCUMENT_STARTERS,
  imageBlob?: Blob,
  gradeLevel = '4th',
  writingLanguage = 'en-us',
  supportLanguage = 'en-us'
): AsyncGenerator<{
  starterId?: string;
  writingPrompt?: string;
  title?: string;
  text: string;
}> {
  // Resolve browser language if needed
  const resolvedWritingLanguage = resolveLanguageCode(writingLanguage);
  const resolvedSupportLanguage = resolveLanguageCode(supportLanguage);

  const text = await textFromTopic(topic);

  const imageDataUrl = imageBlob
    ? await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(imageBlob);
      })
    : undefined;

  const res = await fetch(`${API.defaults.baseURL}/ai/base-document`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/plain',
      'X-Requested-With': 'XMLHttpRequest',
      ...(getCookie('XSRF-TOKEN')
        ? { 'X-XSRF-TOKEN': decodeURIComponent(getCookie('XSRF-TOKEN') as string) }
        : {}),
    },
    body: JSON.stringify({
      topic,
      templates: templates.map((t) => ({
        starterId: t.id,
        title: getWritingPlan(t.writingPlanId)?.title,
        description: getWritingPlan(t.writingPlanId)?.description,
      })),
      grade_level: gradeLevel,
      writing_language: resolvedWritingLanguage,
      support_language: resolvedSupportLanguage,
      image_data_url: imageDataUrl,
    }),
    credentials: 'include',
  });

  const reader = res.body?.getReader();
  if (!reader) return;
  let partialResult = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    partialResult += new TextDecoder().decode(value);
    try {
      const choice: {
        starterId?: string;
        writingPrompt?: string;
        title?: string;
        imageSummary?: string;
      } = JSON.parse(jsonAutocomplete(partialResult));
      yield {
        ...choice,
        text: text + (choice.imageSummary ? `\n\n${choice.imageSummary}` : ''),
      };
      await new Promise((resolve) => setTimeout(resolve, Math.random() * 100));
    } catch {}
  }
}

export async function* generateSentenceCompletions(
  text: string,
  blockTypes: BlockType[],
  writingPlan: WritingPlan | null,
  assignment: Assignment,
  gradeLevel = '4th',
): AsyncGenerator<{ index: number; starter: string }[]> {
  const blocksToFillIn = blockTypes
    .map((type, index) => ({
      originalIndex: index,
      ...type,
    }))
    .filter(
      (type) => type.configuration.hasText && type.id !== BlockTypesEnum.Title,
    );

  const res = await fetch(`${API.defaults.baseURL}/ai/sentence-starters`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/plain',
      'X-Requested-With': 'XMLHttpRequest',
      ...(getCookie('XSRF-TOKEN')
        ? { 'X-XSRF-TOKEN': decodeURIComponent(getCookie('XSRF-TOKEN') as string) }
        : {}),
    },
    body: JSON.stringify({
      text,
      grade_level: gradeLevel,
      writing_plan_title: writingPlan?.title || 'free write',
      paragraphs: blocksToFillIn.map((type, n) => ({
        paragraphNumber: n + 1,
        type: type.title,
      })),
      writing_prompt: assignment.instructions || '',
    }),
    credentials: 'include',
  });

  const reader = res.body?.getReader();
  if (!reader) return;
  let partialResult = '';
  // eslint-disable-next-line no-restricted-syntax
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    partialResult += new TextDecoder().decode(value);
    try {
      const starters: {
        sentenceStarters?: {
          paragraphNumber?: number;
          starter?: string;
        }[];
      } = JSON.parse(jsonAutocomplete(partialResult));

      const result: { index: number; starter: string }[] = [];
      if (Array.isArray(starters?.sentenceStarters)) {
        for (const starter of starters.sentenceStarters) {
          if (starter.paragraphNumber) {
            const number = parseInt(starter.paragraphNumber.toString(), 10);
            const block = blocksToFillIn[number - 1];
            if (block) {
              result.push({
                index: block.originalIndex,
                starter: starter.starter || '',
              });
            }
          }
        }
      }
      yield result;
    } catch (e) {}
  }
}

export async function suggestWritingPrompt(
  topic: string,
  gradeLevel = '4th',
  skills: string[] = []
) {
  return API.post<{
    data: {
      text: string;
    }[];
  }>('/ai/writing-prompts', {
    topic,
    grade_level: gradeLevel,
    writing_plan: skills[0],
  });
}

export async function askWritingBuddy(
  paragraphs: {
    text: string;
    blockName: string;
  }[],
  currentBlockName: string,
  writingPlanTitle?: string,
  availableBlockNames?: string[],
  instructions?: string,
  gradeLevel?: string,
  writingLanguage?: string,
  supportLanguage?: string,
) {
  // Resolve browser language if needed
  const resolvedWritingLanguage = writingLanguage
    ? resolveLanguageCode(writingLanguage)
    : 'en-US';
  const resolvedSupportLanguage = supportLanguage
    ? resolveLanguageCode(supportLanguage)
    : 'en-US';

  return API.post<{
    data: {
      text: string;
      prompt?: string;
      entities: any[];
    }[];
  }>('/ai/writing-hints', {
    paragraphs,
    current_block_name: currentBlockName,
    writing_plan: writingPlanTitle,
    available_block_names: availableBlockNames,
    instructions: instructions || undefined,
    grade_level: gradeLevel || '4th',
    writing_language: resolvedWritingLanguage,
    support_language: resolvedSupportLanguage,
  });
}
